/**
 * @fileoverview CategorySummary component displays spending breakdown by category
 * with collapsible details for better mobile UX. Shows active categories for the
 * selected month with clickable boxes to expand/collapse Start/Credits/Debits details.
 */

import { useState } from 'react';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import styles from './CategorySummary.module.css';
import { formatCurrency } from '../../utils/moneyUtils';

/**
 * CategorySummary Component
 *
 * Displays category balance breakdown for the current month.
 * Shows each category with its ending balance and detailed balance information.
 * Categories can be clicked to expand/collapse detailed breakdown.
 *
 * Props:
 * - spendingByCategory (array): Array of objects with {category, total} structure
 * - selectedMonth (Date): The currently selected month
 * - categoryObjects (array): Array of category objects with active dates
 * - categoryBalances (object): Object keyed by category name with balance data
 * - getBalanceForCategory (function): Function to get balance for a specific category
 * - singleBankMode (boolean): Whether budget is in single bank mode
 * - isLoading (boolean): Whether transaction data is currently loading
 */

function CategorySummary({ spendingByCategory, selectedMonth, categoryObjects, _categoryBalances, getBalanceForCategory, singleBankMode = false, isLoading = false }) {
    // Track which categories are expanded (all collapsed by default)
    const [expandedCategories, setExpandedCategories] = useState({});

    const toggleCategory = (categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    // Filter to only show categories active for the selected month
    const getActiveCategoriesForMonth = () => {
        if (!categoryObjects || categoryObjects.length === 0) {
            return spendingByCategory;
        }

        // Use middle of month to check if category is active
        const monthDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 15);

        const activeCategoryNames = categoryObjects.filter(cat => {
            const startDate = new Date(cat.startDate);
            const endDate = cat.endDate ? new Date(cat.endDate) : null;

            if (monthDate < startDate) return false;
            if (endDate && monthDate > endDate) return false;

            // Filter out user categories in single bank mode
            if (singleBankMode && (cat.userKey !== null && cat.userKey !== undefined)) return false;

            return true;
        }).map(cat => cat.categoryName);

        // Filter spending data to only active categories
        return spendingByCategory.filter(item =>
            activeCategoryNames.includes(item.category)
        );
    };

    const activeSpending = getActiveCategoriesForMonth();

    // Show loading skeleton while fetching transaction data
    if (isLoading) {
        return (
            <div className={styles.container}>
                <h3 className={styles.title}>Spending by Category</h3>
                <LoadingSkeleton variant="card" count={3} />
            </div>
        );
    }

    // Show empty state only after loading is complete
    if (activeSpending.length === 0) {
        return (
            <div className={styles.container}>
                <h3 className={styles.title}>Spending by Category</h3>
                <p className={styles.emptyMessage}>No transactions this month</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Spending by Category</h3>
            {activeSpending.map(item => {
                const balance = getBalanceForCategory ? getBalanceForCategory(item.category) : {
                    startingBalance: 0,
                    credits: 0,
                    debits: 0,
                    endingBalance: 0
                };

                // Determine if category has activity: light grey only if ending balance, credits, and debits are all zero
                const hasActivity = balance.endingBalance !== 0 || balance.credits !== 0 || balance.debits !== 0;
                const isNegative = balance.endingBalance < 0;
                const isExpanded = expandedCategories[item.category] || false;

                // Build CSS class names
                const itemClasses = [
                    styles.categoryItem,
                    hasActivity ? styles.hasActivity : styles.noActivity,
                    isNegative ? styles.negativeBalance : ''
                ].filter(Boolean).join(' ');

                return (
                    <div
                        key={item.category}
                        className={itemClasses}
                        onClick={() => toggleCategory(item.category)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && toggleCategory(item.category)}
                    >
                        <div className={styles.categoryMain}>
                            <span>
                                <strong>{item.category}:</strong> {formatCurrency(balance.endingBalance)}
                            </span>
                            <span className={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
                        </div>
                        <div className={`${styles.categoryDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                            <div className={styles.detailRow}>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}>Start:</span>
                                    <span className={styles.value}>{formatCurrency(balance.startingBalance)}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}>Credits:</span>
                                    <span className={styles.value}>{formatCurrency(balance.credits)}</span>
                                </div>
                            </div>
                            <div className={styles.detailRow}>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}>Net:</span>
                                    <span className={styles.value}>{formatCurrency(balance.credits + balance.debits)}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <span className={styles.label}>Debits:</span>
                                    <span className={styles.value}>{formatCurrency(balance.debits)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default CategorySummary;